import { applyFormat } from "components/ui/ui-helpers";
import Column from "components/ui/layout/column/column";
import CardBox from "components/ui/controls/card-box/card-box";

const BRACKETS = [
  { rate: 0,  label: "0%",  color: "#10b981", darkColor: "#047857" },
  { rate: 11, label: "11%", color: "#06b6d4", darkColor: "#0e7490" },
  { rate: 30, label: "30%", color: "#f59e0b", darkColor: "#b45309" },
  { rate: 41, label: "41%", color: "#ef4444", darkColor: "#b91c1c" },
  { rate: 45, label: "45%", color: "#dc2626", darkColor: "#7f1d1d" },
];

const fmt = (val) => applyFormat(val, "uint", { thousands: " ", decimal: ",", unit: " €" });
const decimal = (val) => applyFormat(val, "float", { thousands: " ", decimal: ",", precision: 2 });


const DetailTMI = (props) => {
  const { DetailImpot } = props;

  if (!DetailImpot?.Dirigeant) return null;

  const {
    RevenuTranche1 = 0,
    RevenuTranche2 = 0,
    RevenuTranche3 = 0,
    RevenuTranche4 = 0,
    Tmi = 0,
    Tranche1 = 11600,
    Tranche2 = 29579,
    Tranche3 = 84577,
    Tranche4 = 181917,
  } = DetailImpot;

  // Revenue fraction per bracket — RevenuTranche values are TAX amounts, so: revenu = tax / rate
  const revenues = [
    Tranche1,
    RevenuTranche1 > 0 ? Math.round(RevenuTranche1 / 0.11) : 0,
    RevenuTranche2 > 0 ? Math.round(RevenuTranche2 / 0.30) : 0,
    RevenuTranche3 > 0 ? Math.round(RevenuTranche3 / 0.41) : 0,
    RevenuTranche4 > 0 ? Math.round(RevenuTranche4 / 0.45) : 0,
  ];

  const tmiIdx = BRACKETS.findIndex((b) => b.rate === Tmi);

  // Full capacity of each bracket (used to compute fill % within the TMI segment)
  const bracketRanges = [
    Tranche1,
    Tranche2 - Tranche1,
    Tranche3 - Tranche2,
    Tranche4 - Tranche3,
    500000, // 45% has no upper limit
  ];
  const tmiFillPct = tmiIdx >= 0 && bracketRanges[tmiIdx] > 0
    ? Math.min(revenues[tmiIdx] / bracketRanges[tmiIdx], 1)
    : 1;

  // Le seuil de déduction tient compte du croisement des deux calculs du quotient familial.
  const deduction = DetailImpot.DeductionTMI;
  const montantReduction = deduction?.Montant ?? 0;
  const afficherDeduction = Number.isFinite(montantReduction) && montantReduction > 0
    && Number.isFinite(deduction?.TauxAvant) && Number.isFinite(deduction?.TauxApres)
    && deduction.TauxAvant > deduction.TauxApres;

  return (
    <Column fill>
      <CardBox>
        <Column fill>
          <div style={{ padding: "20px" }}>

            {/* ── Header ────────────────────────────────────────── */}
            <div style={{
              background: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
              borderRadius: "14px",
              padding: "18px 24px",
              marginBottom: "28px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              boxShadow: "0 4px 18px rgba(30,58,95,0.28)",
            }}>
              <div>
                <div style={{
                  color: "#93c5fd",
                  fontSize: "11px",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  marginBottom: "6px",
                }}>
                  Barème progressif de l'impôt sur le revenu
                </div>
                <div style={{ color: "#ffffff", fontSize: "18px", fontWeight: 700 }}>
                  Votre tranche marginale d'imposition
                </div>
              </div>
              <div style={{
                borderRadius: "50px",
                padding: "10px 24px",
                color: "#ffffff",
                fontSize: "20px",
                fontWeight: 800,
                textAlign: "center",
                minWidth: "90px",
              }}>
                {Tmi}%
              </div>
            </div>

            {/* ── Timeline Bar ──────────────────────────────────── */}
            <div  style={{
              display: "flex",
              height: "64px",
              borderRadius: "16px",
              overflow: "hidden",
              boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
              gap: "3px",
              marginBottom: "6px",
              marginTop: "100px",
            }}>
              {BRACKETS.map((cfg, idx) => {
                const rev = revenues[idx];
                const active = rev > 0;
                const isTmi = cfg.rate === Tmi;
                const fillColor = "#1e4d8c";
                const background = isTmi
                  ? `linear-gradient(90deg, ${fillColor} ${tmiFillPct * 100}%, #d1d5db ${tmiFillPct * 100}%)`
                  : active
                    ? fillColor
                    : "linear-gradient(160deg, #e5e7eb, #d1d5db)";
                return (
                  <div
                    key={cfg.rate}
                    style={{
                      flex: active ? rev : "0 0 52px",
                      background,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexDirection: "column",
                      gap: "3px",
                      minWidth: "36px",
                    }}
                  >
                    <span style={{
                      color: active ? "#ffffff" : "#9ca3af",
                      fontWeight: isTmi ? 800 : 600,
                      fontSize: isTmi ? "17px" : "13px",
                      textShadow: active ? "0 1px 3px rgba(0,0,0,0.2)" : "none",
                      lineHeight: 1,
                    }}>
                      {cfg.label}
                    </span>
                    {active && rev > 8000 && (
                      <span style={{
                        color: isTmi ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.75)",
                        fontSize: "10px",
                        fontWeight: 500,
                      }}>
                        {Math.round(rev / 1000)}k€
                      </span>
                    )}
                  </div>
                );
              })}
            </div>

            {/* ── TMI Pill — mirrors bar flex so it aligns ──────── */}
            <div style={{ display: "flex", marginBottom: "24px" }}>
              {BRACKETS.map((cfg, idx) => {
                const rev = revenues[idx];
                const active = rev > 0;
                const isTmi = cfg.rate === Tmi;
                return (
                  <div
                    key={cfg.rate}
                    style={{
                      flex: active ? rev : "0 0 52px",
                      minWidth: "36px",
                      display: "flex",
                      justifyContent: "center",
                      paddingTop: "7px",
                    }}
                  >
                    {isTmi && (
                      <div style={{
                        background: `linear-gradient(135deg, ${cfg.color}, ${cfg.darkColor})`,
                        color: "#fff",
                        fontSize: "10px",
                        fontWeight: 700,
                        padding: "3px 12px",
                        borderRadius: "20px",
                        textTransform: "uppercase",
                        letterSpacing: "0.07em",
                        boxShadow: `0 3px 10px ${cfg.color}55`,
                        whiteSpace: "nowrap",
                      }}>
                        ▲ Votre TMI
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* ── Phrases d'information tranche ───────────────── */}
            <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginTop: "100px" }}>

              {afficherDeduction && (
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "14px",
                  padding: "14px 18px",
                  borderRadius: "12px",
                  background: "#06b6d40f",
                  border: "1.5px solid #06b6d433",
                }}>
                  <div style={{
                    width: "38px",
                    height: "38px",
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, #06b6d4, #0e7490)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    fontSize: "18px",
                    color: "#fff",
                  }}>
                    ↓
                  </div>
                  <div style={{ fontSize: "13px", color: "#374151", lineHeight: 1.6 }}>
                    Un versement déductible de{" "}
                    <strong style={{ color: "#06b6d4" }}>{fmt(montantReduction)}</strong>
                    {" "}permettrait de bénéficier d'une économie d'impôt de{" "}
                    <strong>{deduction.TauxAvant} %</strong> sur cette somme. Au-delà, l'économie fiscale passerait à{" "}
                    <strong>{deduction.TauxApres} %</strong>, compte tenu du quotient familial.
                    <div style={{ marginTop: "8px" }}>
                      <em>Ce montant est indicatif et ne tient pas compte des plafonds de déduction d'épargne retraite éventuellement disponibles.</em>
                    </div>
                    {deduction.PlafonnementActif && (
                      <details style={{ marginTop: "12px" }}>
                        <summary style={{ cursor: "pointer", fontWeight: 600 }}>Comprendre le calcul</summary>
                        <p>
                          Pour une assiette imposable de {fmt(deduction.BaseImposable)}, le calcul A correspond à l'impôt
                          sur {deduction.PartsSansEnfant} part(s), diminué du plafond du quotient familial
                          de {fmt(deduction.PlafondQuotientFamilial)}
                          {deduction.ReductionComplementaireVeuf > 0 && ` et de la réduction complémentaire veuf de ${fmt(deduction.ReductionComplementaireVeuf)}`}.
                          {" "}Le calcul B retient les {deduction.PartsAvecEnfants} part(s) du foyer. L'impôt retenu est le plus élevé des deux.
                        </p>
                        <p>
                          A = {decimal(deduction.ImpotA)} € ; B = {decimal(deduction.ImpotB)} €.
                          {" "}L'écart est de {decimal(deduction.EcartImpots)} €.
                        </p>
                        <p>
                          A est dans la tranche à {deduction.TauxA} % et B dans celle à {deduction.TauxB} %.
                          {" "}Pour chaque euro déduit, A diminue de {decimal(deduction.TauxA / 100)} €
                          et B de {decimal(deduction.TauxB / 100)} €.
                          {deduction.EcartTaux > 0 ? (
                            <>
                              {" "}L'écart diminue donc de {decimal(deduction.TauxA / 100)} − {decimal(deduction.TauxB / 100)}
                              {" "}= <strong>{decimal(deduction.EcartTaux / 100)} €</strong> par euro déduit,
                              soit <strong>{fmt(1000 * deduction.EcartTaux / 100)}</strong> pour 1 000 € de versement déductible.
                            </>
                          ) : (
                            <> L'écart reste constant tant que les deux calculs restent dans ces tranches. Il ne permet donc pas de déterminer le versement par une division par la différence des taux.</>
                          )}
                        </p>
                        <p>
                          {deduction.EcartTaux > 0 && Math.abs(deduction.EcartImpots / (deduction.EcartTaux / 100) - montantReduction) < 0.05 && (
                            <>Le versement correspondant est donc de {decimal(deduction.EcartImpots)} € ÷ {decimal(deduction.EcartTaux / 100)} ≈ {fmt(montantReduction)}. </>
                          )}
                          Le premier passage de {deduction.TauxAvant} % à {deduction.TauxApres} % intervient à une assiette imposable
                          de <strong>{decimal(deduction.BaseImposable - montantReduction)} €</strong>, soit{" "}
                          {decimal(deduction.BaseImposable)} € − {decimal(deduction.BaseImposable - montantReduction)} €
                          {" "}= <strong>{decimal(montantReduction)} €</strong> de déduction.
                          {" "}Le seuil affiché tient compte des éventuels changements de tranche de A et B jusqu'à cette première baisse de taux.
                          Ce calcul repose sur le barème, avant décote et réductions ou crédits d'impôt.
                        </p>
                      </details>
                    )}
                  </div>
                </div>
              )}

            </div>

          </div>
        </Column>
      </CardBox>
    </Column>
  );
};

export default DetailTMI;

